import { collection, doc, setDoc, getDoc, getDocs, query, where, Timestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { PrimaryCamper, CAMPERS_COLLECTION, Dependant, Note } from '../types/camper';

// Get all active (non-deleted) campers
export const getAllCampers = async (): Promise<PrimaryCamper[]> => {
  try {
    const q = query(
      collection(db, CAMPERS_COLLECTION),
      where('deleted', '!=', true)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as PrimaryCamper);
  } catch (error) {
    console.error('Error getting campers:', error);
    throw error;
  }
};

// Create a new primary camper record
export const createPrimaryCamper = async (camperData: Omit<PrimaryCamper, 'recordId' | 'createdDate' | 'lastModifiedDate'>) => {
  try {
    const newCamperRef = doc(collection(db, CAMPERS_COLLECTION));
    const timestamp = Timestamp.now();

    const newCamper: PrimaryCamper = {
      ...camperData,
      recordId: newCamperRef.id,
      createdDate: timestamp.toDate(),
      lastModifiedDate: timestamp.toDate(),
      notes: [], // Initialize empty notes array
      deleted: false // Initialize as not deleted
    };

    await setDoc(newCamperRef, newCamper);
    return newCamper;
  } catch (error) {
    console.error('Error creating primary camper:', error);
    throw error;
  }
};

// Get a primary camper by ID
export const getPrimaryCamper = async (recordId: string): Promise<PrimaryCamper | null> => {
  try {
    const camperDoc = await getDoc(doc(db, CAMPERS_COLLECTION, recordId));
    if (!camperDoc.exists() || camperDoc.data()?.deleted === true) {
      return null;
    }
    return camperDoc.data() as PrimaryCamper;
  } catch (error) {
    console.error('Error getting primary camper:', error);
    throw error;
  }
};

// Update a primary camper record
export const updatePrimaryCamper = async (recordId: string, updates: Partial<PrimaryCamper>) => {
  try {
    const camperRef = doc(db, CAMPERS_COLLECTION, recordId);
    const timestamp = Timestamp.now();

    await setDoc(camperRef, {
      ...updates,
      lastModifiedDate: timestamp.toDate()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating primary camper:', error);
    throw error;
  }
};

// Add a note to a primary camper
export const addNote = async (recordId: string, content: string, createdBy: string, isPersistent: boolean) => {
  try {
    const camperRef = doc(db, CAMPERS_COLLECTION, recordId);
    const camperDoc = await getDoc(camperRef);

    if (!camperDoc.exists()) {
      throw new Error('Primary camper not found');
    }

    const timestamp = Timestamp.now();
    const newNote: Note = {
      id: Date.now().toString(), // Simple ID generation
      content,
      createdDate: timestamp.toDate(),
      lastModifiedDate: timestamp.toDate(),
      createdBy,
      isPersistent
    };

    const camperData = camperDoc.data() as PrimaryCamper;
    const updatedNotes = [...(camperData.notes || []), newNote];

    await updatePrimaryCamper(recordId, { notes: updatedNotes });
    return newNote;
  } catch (error) {
    console.error('Error adding note:', error);
    throw error;
  }
};

// Update a note
export const updateNote = async (recordId: string, noteId: string, content: string, isPersistent: boolean) => {
  try {
    const camperRef = doc(db, CAMPERS_COLLECTION, recordId);
    const camperDoc = await getDoc(camperRef);

    if (!camperDoc.exists()) {
      throw new Error('Primary camper not found');
    }

    const camperData = camperDoc.data() as PrimaryCamper;
    const timestamp = Timestamp.now();

    const updatedNotes = camperData.notes.map(note => 
      note.id === noteId 
        ? { ...note, content, lastModifiedDate: timestamp.toDate(), isPersistent }
        : note
    );

    await updatePrimaryCamper(recordId, { notes: updatedNotes });
  } catch (error) {
    console.error('Error updating note:', error);
    throw error;
  }
};

// Delete a note
export const deleteNote = async (recordId: string, noteId: string) => {
  try {
    const camperRef = doc(db, CAMPERS_COLLECTION, recordId);
    const camperDoc = await getDoc(camperRef);

    if (!camperDoc.exists()) {
      throw new Error('Primary camper not found');
    }

    const camperData = camperDoc.data() as PrimaryCamper;
    const updatedNotes = camperData.notes.filter(note => note.id !== noteId);

    await updatePrimaryCamper(recordId, { notes: updatedNotes });
  } catch (error) {
    console.error('Error deleting note:', error);
    throw error;
  }
};

// Add a dependant to a primary camper
export const addDependant = async (recordId: string, dependant: Dependant) => {
  try {
    const camperRef = doc(db, CAMPERS_COLLECTION, recordId);
    const camperDoc = await getDoc(camperRef);

    if (!camperDoc.exists()) {
      throw new Error('Primary camper not found');
    }

    const camperData = camperDoc.data() as PrimaryCamper;
    const updatedDependants = [...camperData.dependants, dependant];

    await updatePrimaryCamper(recordId, { dependants: updatedDependants });
  } catch (error) {
    console.error('Error adding dependant:', error);
    throw error;
  }
};

// Remove a dependant from a primary camper
export const removeDependant = async (recordId: string, dependantIndex: number) => {
  try {
    const camperRef = doc(db, CAMPERS_COLLECTION, recordId);
    const camperDoc = await getDoc(camperRef);

    if (!camperDoc.exists()) {
      throw new Error('Primary camper not found');
    }

    const camperData = camperDoc.data() as PrimaryCamper;
    const updatedDependants = camperData.dependants.filter((_, index) => index !== dependantIndex);

    await updatePrimaryCamper(recordId, { dependants: updatedDependants });
  } catch (error) {
    console.error('Error removing dependant:', error);
    throw error;
  }
};

// Delete all campers from the collection
export const deleteAllCampers = async () => {
  try {
    const camperSnapshot = await getDocs(collection(db, CAMPERS_COLLECTION));
    const deletePromises = camperSnapshot.docs.map(doc => 
      deleteDoc(doc.ref)
    );
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error deleting all campers:', error);
    throw error;
  }
};

// Get all deleted campers
export const getDeletedCampers = async (): Promise<PrimaryCamper[]> => {
  try {
    const q = query(
      collection(db, CAMPERS_COLLECTION),
      where('deleted', '==', true)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as PrimaryCamper);
  } catch (error) {
    console.error('Error getting deleted campers:', error);
    throw error;
  }
};

// Restore a deleted camper
export const restoreCamper = async (recordId: string) => {
  try {
    const timestamp = Timestamp.now();
    const camperRef = doc(db, CAMPERS_COLLECTION, recordId);
    
    await setDoc(camperRef, {
      deleted: false,
      deletedDate: null,
      lastModifiedDate: timestamp.toDate()
    }, { merge: true });
  } catch (error) {
    console.error('Error restoring camper:', error);
    throw error;
  }
};

// Restore all deleted campers
export const restoreAllCampers = async () => {
  try {
    const timestamp = Timestamp.now();
    const deletedCampersSnapshot = await getDocs(
      query(collection(db, CAMPERS_COLLECTION), where('deleted', '==', true))
    );
    
    const restorePromises = deletedCampersSnapshot.docs.map(doc => 
      setDoc(doc.ref, {
        deleted: false,
        deletedDate: null,
        lastModifiedDate: timestamp.toDate()
      }, { merge: true })
    );
    
    await Promise.all(restorePromises);
  } catch (error) {
    console.error('Error restoring all campers:', error);
    throw error;
  }
}; 